import { useState } from "react";
import { X, Calendar, Clock, AlertCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import DateCalendar from "../../DateCalendar";

interface RescheduleTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: { date: Date; time: string; reason: string }) => void;
  treatment?: {
    id: string;
    clientName: string;
    treatmentName: string;
    currentDate: string;
    currentTime: string;
  };
}

const rescheduleReasons = [
  { value: "client_request", label: "Client requested" },
  { value: "practitioner_unavailable", label: "Practitioner unavailable" },
  { value: "emergency", label: "Emergency" },
  { value: "scheduling_conflict", label: "Scheduling conflict" },
  { value: "other", label: "Other" },
];

export default function RescheduleTreatmentModal({
  isOpen,
  onClose,
  onSave,
  treatment,
}: RescheduleTreatmentModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [selectedReason, setSelectedReason] = useState("");
  const [notes, setNotes] = useState("");
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [notifyClient, setNotifyClient] = useState(true);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleSave = () => {
    onSave?.({
      date: selectedDate,
      time: selectedTime,
      reason: selectedReason || notes,
    });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setSelectedDate(new Date());
    setSelectedTime("10:00");
    setSelectedReason("");
    setNotes("");
    setNotifyClient(true);
  };

  const isValid = selectedDate && selectedTime;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <div>
            <h2 className="text-lg font-bold text-[#2d2d2d]">Reschedule Treatment</h2>
            {treatment && (
              <p className="text-sm text-[#6b6b6b]">
                {treatment.treatmentName} for {treatment.clientName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f8f5fc] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5">
          {/* Current Appointment Info */}
          {treatment && (
            <div className="p-3 bg-[#fff8e6] border border-[#ffd89e] rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-[#e6a700] mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#8a6d00]">Current Appointment</p>
                  <p className="text-sm text-[#8a6d00]">
                    {treatment.currentDate} at {treatment.currentTime}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* New Date & Time */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              New Date & Time
            </label>
            <div className="flex gap-3">
              {/* Date Picker */}
              <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                <PopoverTrigger asChild>
                  <button className="flex-1 flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] hover:border-[#704aaf] transition-colors">
                    <Calendar className="w-4 h-4 text-[#704aaf]" />
                    <span className="truncate">{formatDate(selectedDate)}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[1200] bg-white border border-[#e8e0f0] shadow-xl rounded-xl">
                  <DateCalendar
                    selectedDate={selectedDate}
                    onSelectDate={(date) => {
                      setSelectedDate(date);
                      setIsDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>

              {/* Time Input */}
              <div className="flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg hover:border-[#704aaf] transition-colors">
                <Clock className="w-4 h-4 text-[#704aaf]" />
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="text-sm text-[#3d3d3d] outline-none w-20"
                />
              </div>
            </div>
          </div>

          {/* Reason for Rescheduling */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Reason for Rescheduling
            </label>
            <div className="space-y-2">
              {rescheduleReasons.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedReason === reason.value
                      ? "border-[#704aaf] bg-[#f8f5fc]"
                      : "border-[#e8e0f0] hover:bg-[#faf8fc]"
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="w-4 h-4 text-[#704aaf] border-[#e8e0f0] focus:ring-[#704aaf]"
                  />
                  <span className="text-sm text-[#3d3d3d]">{reason.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Notes */}
          {selectedReason === "other" && (
            <div>
              <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
                Additional Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Please specify the reason..."
                className="w-full px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
              />
            </div>
          )}

          {/* Notify Client */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="notify-client"
              checked={notifyClient}
              onChange={(e) => setNotifyClient(e.target.checked)}
              className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
            />
            <label htmlFor="notify-client" className="text-sm text-[#5c5c5c]">
              Notify client about the reschedule
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#6b6b6b] hover:bg-[#f0ebf5] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid}
              className="px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reschedule Treatment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


